/**
 * TaskWorkflowMapTab — Full workflow DAG minimap using Cytoscape.js.
 *
 * Renders ALL sibling tasks (same workflow instance) as a directed graph.
 * - Current task highlighted with accent ring
 * - Task nodes colored by status (from shared task-status-colors.ts)
 * - Edges derived from dependencies[] arrays
 * - Click node → navigate to that task
 * - Progress bar: "7 of 12 completed"
 * - Layout: dagre-like breadthfirst with directed flow
 *
 * Only visible for: task primitive type (via CONTEXTUAL_TABS).
 *
 * DUMB FRONTEND: All data from TaskFieldsFragment arrays. No hardcoded assumptions.
 */

import { useEffect, useRef, useMemo } from 'react';
import {
  Stack,
  Text,
  Center,
  Group,
  Badge,
  Paper,
  Progress,
  Box,
} from '@mantine/core';
import {
  IconMap,
  IconCircleCheck,
  IconPlayerPlay,
  IconClock,
} from '@tabler/icons-react';
import cytoscape, { type Core } from 'cytoscape';
import { GqlTaskStatus, type TaskFieldsFragment } from '@/generated/graphql';
import { getTaskStatusColor, TASK_STATUS_LEGEND } from '../task-status-colors';
import { buildTaskCytoscapeStyles, WORKFLOW_MAP_EDGE_STYLES } from '../cytoscape-task-styles';

// Build styles once at module level (base + workflow-map-specific overrides)
const CYTOSCAPE_STYLES = buildTaskCytoscapeStyles(WORKFLOW_MAP_EDGE_STYLES);

// ============================================================================
// COMPONENT
// ============================================================================

interface TaskWorkflowMapTabProps {
  /** The currently selected task */
  currentTask: TaskFieldsFragment | null;
  /** All sibling tasks in the same workflow instance */
  siblingTasks: TaskFieldsFragment[];
  /** Callback when a node is clicked (navigate to that task) */
  onTaskSelect?: (task: TaskFieldsFragment) => void;
}

export function TaskWorkflowMapTab({
  currentTask,
  siblingTasks,
  onTaskSelect,
}: Readonly<TaskWorkflowMapTabProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);

  // Build task lookup map
  const taskMap = useMemo(() => {
    const map = new Map<string, TaskFieldsFragment>();
    for (const task of siblingTasks) {
      map.set(task.id, task);
    }
    return map;
  }, [siblingTasks]);

  // Build Cytoscape elements from ALL sibling tasks
  const elements = useMemo(() => {
    const nodes: cytoscape.ElementDefinition[] = [];
    const edges: cytoscape.ElementDefinition[] = [];
    const edgeSet = new Set<string>();

    for (const task of siblingTasks) {
      const colors = getTaskStatusColor(task.status);
      const isCurrent = task.id === currentTask?.id;

      nodes.push({
        data: {
          id: task.id,
          label: truncateLabel(task.name, 10),
          bg: colors.bg,
          borderColor: colors.border,
          isCurrent,
          status: task.status,
          sequenceOrder: task.sequenceOrder ?? 0,
        },
      });

      // Edges from dependencies
      for (const depId of task.dependencies) {
        if (taskMap.has(depId)) {
          const edgeKey = `${depId}->${task.id}`;
          if (!edgeSet.has(edgeKey)) {
            edgeSet.add(edgeKey);
            const isActive =
              task.id === currentTask?.id || depId === currentTask?.id;
            edges.push({
              data: {
                id: `edge-${edgeKey}`,
                source: depId,
                target: task.id,
                isActive,
              },
            });
          }
        }
      }
    }

    // If no dependency edges exist, create a sequence chain by sequenceOrder
    if (edges.length === 0 && nodes.length > 1) {
      const sorted = [...siblingTasks].sort((a, b) => {
        const aOrder = a.sequenceOrder ?? 0;
        const bOrder = b.sequenceOrder ?? 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.createdAt.localeCompare(b.createdAt);
      });
      for (let i = 0; i < sorted.length - 1; i++) {
        const isActive =
          sorted[i]!.id === currentTask?.id ||
          sorted[i + 1]!.id === currentTask?.id;
        edges.push({
          data: {
            id: `seq-${sorted[i]!.id}-${sorted[i + 1]!.id}`,
            source: sorted[i]!.id,
            target: sorted[i + 1]!.id,
            isActive,
          },
        });
      }
    }

    return { nodes, edges };
  }, [siblingTasks, currentTask, taskMap]);

  // Stats
  const stats = useMemo(() => {
    const completed = siblingTasks.filter(
      t => t.status === GqlTaskStatus.COMPLETED
    ).length;
    const inProgress = siblingTasks.filter(
      t => t.status === GqlTaskStatus.IN_PROGRESS
    ).length;
    const pending = siblingTasks.filter(
      t =>
        t.status === GqlTaskStatus.PENDING ||
        t.status === GqlTaskStatus.READY ||
        t.status === GqlTaskStatus.ASSIGNED
    ).length;
    return {
      total: siblingTasks.length,
      completed,
      inProgress,
      pending,
      progressPercent:
        siblingTasks.length > 0
          ? Math.round((completed / siblingTasks.length) * 100)
          : 0,
    };
  }, [siblingTasks]);

  // Initialize Cytoscape
  useEffect(() => {
    if (!containerRef.current || elements.nodes.length === 0) {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
      return;
    }

    if (cyRef.current) {
      cyRef.current.destroy();
      cyRef.current = null;
    }

    const targetSet = new Set(elements.edges.map(e => e.data.target as string));
    const rootIds = elements.nodes
      .filter(n => !targetSet.has(n.data.id as string))
      .map(n => n.data.id as string);

    const cy = cytoscape({
      container: containerRef.current,
      elements: [...elements.nodes, ...elements.edges],
      style: CYTOSCAPE_STYLES,
      layout: {
        name: 'breadthfirst',
        directed: true,
        spacingFactor: 1.8,
        avoidOverlap: true,
        grid: true,
        roots: rootIds.length > 0 ? rootIds : undefined,
      },
      userZoomingEnabled: true,
      userPanningEnabled: true,
      boxSelectionEnabled: false,
      autoungrabify: true,
      minZoom: 0.3,
      maxZoom: 2.5,
    });

    cy.on('tap', 'node', (event) => {
      const nodeId = event.target.id();
      if (nodeId === currentTask?.id) return;
      const task = taskMap.get(nodeId);
      if (task && onTaskSelect) {
        onTaskSelect(task);
      }
    });

    cy.on('mouseover', 'node', (event) => {
      const nodeId = event.target.id();
      if (nodeId !== currentTask?.id) {
        containerRef.current!.style.cursor = 'pointer';
      }
    });
    cy.on('mouseout', 'node', () => {
      containerRef.current!.style.cursor = 'default';
    });

    cyRef.current = cy;

    cy.on('layoutstop', () => {
      cy.fit(undefined, 20);
    });

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [elements, currentTask, taskMap, onTaskSelect]);

  // Empty states
  if (!currentTask) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select a task to view its workflow map.</Text>
      </Center>
    );
  }

  if (siblingTasks.length <= 1) {
    return (
      <Center py="xl">
        <Stack align="center" gap="xs">
          <IconMap size={32} color="var(--mantine-color-dimmed)" />
          <Text c="dimmed" size="sm">Single-task workflow — no map to display.</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="sm" py="xs">
      {/* Progress summary */}
      <Box px="xs">
        <Group justify="space-between" mb={4}>
          <Text size="xs" fw={500}>
            Workflow Progress
          </Text>
          <Text size="xs" c="dimmed">
            {stats.completed} of {stats.total} completed ({stats.progressPercent}%)
          </Text>
        </Group>
        <Progress
          value={stats.progressPercent}
          size="sm"
          color={stats.progressPercent === 100 ? 'green' : 'blue'}
        />
      </Box>

      {/* Stats bar */}
      <Group gap="xs" px="xs">
        <Badge
          variant="light"
          color="green"
          size="sm"
          leftSection={<IconCircleCheck size={12} />}
        >
          {stats.completed} done
        </Badge>
        <Badge
          variant="light"
          color="blue"
          size="sm"
          leftSection={<IconPlayerPlay size={12} />}
        >
          {stats.inProgress} active
        </Badge>
        <Badge
          variant="light"
          color="yellow"
          size="sm"
          leftSection={<IconClock size={12} />}
        >
          {stats.pending} pending
        </Badge>
      </Group>

      {/* Graph container */}
      <Paper
        shadow="xs"
        radius="sm"
        withBorder
        style={{ minHeight: 400 }}
      >
        <Box
          ref={containerRef}
          style={{ width: '100%', height: 400 }}
        />
      </Paper>

      {/* Legend — derived from shared TASK_STATUS_LEGEND */}
      <Group gap="xs" px="xs" wrap="wrap">
        <Text size="xs" c="dimmed" fw={500}>Legend:</Text>
        {TASK_STATUS_LEGEND.map(entry => (
          <LegendItem key={entry.label} color={entry.color} label={entry.label} />
        ))}
        <LegendItem color="#1c7ed6" label="Current (ring)" isBorder />
      </Group>
    </Stack>
  );
}

// ============================================================================
// LEGEND ITEM
// ============================================================================

function LegendItem({
  color,
  label,
  isBorder,
}: Readonly<{
  color: string;
  label: string;
  isBorder?: boolean;
}>) {
  return (
    <Group gap={4}>
      <Box
        w={10}
        h={10}
        style={{
          backgroundColor: isBorder ? 'transparent' : color,
          border: isBorder ? `2px solid ${color}` : undefined,
          borderRadius: 2,
          flexShrink: 0,
        }}
      />
      <Text size="xs" c="dimmed">{label}</Text>
    </Group>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function truncateLabel(name: string, maxLen = 10): string {
  if (name.length <= maxLen) return name;
  return name.slice(0, maxLen - 1) + '…';
}
